import { FC, useCallback } from "react";

import { serverGetSectionScoreReport } from "api/api-server-assessment";
import { SectionScoreTableRow } from "api/api-types-assessment";
import { translateSectionScoreReport } from "components/materials/section/assessment/utils";
import { SectionScoreDisplayFormat, SectionType } from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { useAppSelector } from "store/index";
import { selectCurrentOrg } from "store/selectors";
import { getMaxSectionScoreForOrg } from "utils/assessment";

type SectionScoreTableProps = {
  section: SectionType;
  sectionScoreTableRows: SectionScoreTableRow[] | null;
};

export const SectionScoreTable: FC<SectionScoreTableProps> = ({
  section,
  sectionScoreTableRows,
}) => {
  const currentOrg = useAppSelector(selectCurrentOrg);

  const exportData = useCallback(async () => {
    const reportData = await serverGetSectionScoreReport({ section_id: section.id });
    return translateSectionScoreReport(reportData, true);
  }, [section.id]);

  if (!currentOrg || !sectionScoreTableRows || sectionScoreTableRows.length === 0) {
    return null;
  }

  const displayTotalScoreAsPercentage =
    currentOrg.section_score_display_format === SectionScoreDisplayFormat.Percent;

  const maxActivitiesScore = section.section_score_activities_percentage;
  const maxPrepWorkScore = section.section_score_prepwork_percentage;
  const maxAssignmentsScore = section.section_score_assignments_percentage;

  const maxTotalScore = getMaxSectionScoreForOrg(currentOrg);

  return (
    <ScrollableTableContainer
      exportData={exportData}
      title={t("assessment_dashboard.student_section_scores.title")}
    >
      <Table>
        {/* Headers: Student / Prep Work / In-Class Work / Assignments / Total Section Score */}
        <THead>
          <TR className="border-black-tint-70">
            <TH className="w-16 pl-4">{t("assessment_dashboard.all.student_name_header_title")}</TH>

            {!!maxPrepWorkScore && (
              <TH className="w-16">{t("assessment_dashboard.grades.prepwork")}</TH>
            )}

            {!!maxActivitiesScore && (
              <TH className="w-16">{t("assessment_dashboard.grades.activities")}</TH>
            )}

            {!!maxAssignmentsScore && (
              <TH className="w-16">{t("assessment_dashboard.grades.assignments")}</TH>
            )}

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.tooltip.section_score")}>
                {t("assessment_dashboard.student_section_scores.total_section_score_header_title")}
              </Tooltip>
            </TH>
          </TR>
        </THead>

        <tbody className="font-normal">
          {sectionScoreTableRows.map((row) => (
            <TR key={row.student_id}>
              <TD className="max-w-40 truncate py-2.5 pl-[17px]" title={row.student_name}>
                {row.student_name}
              </TD>

              {!!maxPrepWorkScore && (
                <TD>
                  {row.prep_work_score === null
                    ? "-"
                    : `${row.prep_work_score} / ${maxPrepWorkScore}%`}
                </TD>
              )}

              {!!maxActivitiesScore && (
                <TD>
                  {row.activities_score === null
                    ? "-"
                    : `${row.activities_score} / ${maxActivitiesScore}%`}
                </TD>
              )}

              {!!maxAssignmentsScore && (
                <TD>
                  {row.assignments_score === null
                    ? "-"
                    : `${row.assignments_score} / ${maxAssignmentsScore}%`}
                </TD>
              )}

              {(row.prep_work_score === null && !!maxPrepWorkScore) ||
              (row.activities_score === null && !!maxActivitiesScore) ||
              (row.assignments_score === null && !!maxAssignmentsScore) ? (
                <TD>-</TD>
              ) : displayTotalScoreAsPercentage ? (
                <TD>{row.total_section_score}%</TD>
              ) : (
                <TD>
                  {row.total_section_score} / {maxTotalScore}
                </TD>
              )}
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
